import { Paper, Stack, Typography } from '@mui/material'
import { C1Button } from 'c1ui'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { shadows } from 'c1ui/theme/C1Theme'
import { C1Icon } from 'components/core/icons/C1Icon'
import { SafeMarkdown } from 'components/core/markdown'
import { PolicyTypeItemView } from 'components/pages/policy/list/table'
import { Check } from 'lucide-react'
import { Policy } from 'pbts/c1/api/policy/v1/policy'
import { FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { PolicyDraftPreview } from './PolicyDraftPreview'

type PolicyActionItemProps = {
    policy: Policy
    explanation: string
    isNew: boolean
    onApply?: () => void
}

/**
 * Renders a policy draft result from the AI assistant.
 * Displays the policy header, full configuration preview, explanation, and an "Apply" button.
 *
 * Uses PolicyDraftPreview to show the complete policy structure with all rules and steps,
 * matching the same components used in the policy detail page.
 */
export const PolicyActionItem: FC<PolicyActionItemProps> = ({
    policy,
    explanation,
    isNew,
    onApply,
}) => {
    const intl = useIntl()

    // Don't render if there's nothing to show and no action
    if (!policy.displayName && !onApply) {
        return null
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                bgcolor: 'common.white',
                borderRadius: C1BorderRadius.large,
                boxShadow: shadows.default,
            }}
        >
            <Stack spacing={2}>
                {/* Header with policy type badge and name */}
                <Stack direction="row" spacing={2} alignItems="center">
                    <PolicyTypeItemView type={policy.policyType} />
                    <Typography variant="h6" component="span">
                        {policy.displayName || intl.formatMessage({
                            id: 'copilot_policy_untitled',
                            defaultMessage: 'Untitled policy',
                        })}
                    </Typography>
                </Stack>

                {/* Description if present */}
                {policy.description && (
                    <Typography color="text.secondary">
                        {policy.description}
                    </Typography>
                )}

                {/* Full policy configuration preview */}
                <PolicyDraftPreview policy={policy} />

                {/* Explanation from the AI */}
                {explanation && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        sx={{
                            '& p': {
                                margin: 0,
                            },
                            '& p:not(:last-child)': {
                                marginBottom: 1,
                            },
                        }}
                    >
                        <SafeMarkdown>{explanation}</SafeMarkdown>
                    </Typography>
                )}

                {/* Apply button */}
                {onApply && (
                    <C1Button
                        variant="contained"
                        color="primary"
                        onClick={onApply}
                        startIcon={<C1Icon icon={Check} />}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        {isNew ? (
                            <FormattedMessage
                                id="copilot_policy_apply_create"
                                defaultMessage="Apply to editor"
                            />
                        ) : (
                            <FormattedMessage
                                id="copilot_policy_apply_edit"
                                defaultMessage="Apply changes"
                            />
                        )}
                    </C1Button>
                )}
            </Stack>
        </Paper>
    )
}
